// Central registry of route → dynamic import.
// Used to warm up route chunks on link hover/focus/visibility so navigation
// feels instant instead of waiting on a chunk download mid-click.
//
// IMPORTANT: keep the import() expressions string-identical to the ones in
// App.tsx so Vite produces the same chunk and the cache hits.

type Loader = () => Promise<unknown>;

const loaders: Record<string, Loader> = {
  "/services": () => import("@/pages/Services"),
  "/products": () => import("@/pages/Services"),
  "/about": () => import("@/pages/About"),
  "/newsletter": () => import("@/pages/Newsletter"),
  "/shubham-borkar": () => import("@/pages/Founder"),
  "/ai-investing-club": () => import("@/pages/ResearchInAction"),
  "/ai-investing-club/community": () => import("@/pages/Community"),
  "/login": () => import("@/pages/Login"),
  "/dashboard": () => import("@/pages/Dashboard"),
  "/admin": () => import("@/pages/Admin"),
  
  "/ai-financial-research": () => import("@/pages/AiFinancialResearch"),
  "/free-discovery-call": () => import("@/pages/FreeDiscoveryCall"),
  "/legal": () => import("@/pages/Legal"),
  "/privacy-policy": () => import("@/pages/Legal"),
  "/terms": () => import("@/pages/Legal"),
  "/refund-policy": () => import("@/pages/Legal"),
  "/risk-disclosure": () => import("@/pages/Legal"),
  "/disclaimer": () => import("@/pages/Legal"),
  "/ai-research-consultation": () => import("@/pages/AiResearchConsultation"),
  "/command-centre-pro": () => import("@/pages/AiResearchConsultation"),
  "/enterprise": () => import("@/pages/Enterprise"),
  "/research-desk": () => import("@/pages/ResearchDesk"),
};

const started = new Set<string>();

function normalize(path: string): string {
  // strip query/hash, drop trailing slash (except root)
  const noHash = path.split("#")[0].split("?")[0];
  if (noHash.length > 1 && noHash.endsWith("/")) return noHash.slice(0, -1);
  return noHash;
}

export function prefetchRoute(path: string): void {
  if (typeof window === "undefined") return;
  const key = normalize(path);
  if (started.has(key)) return;
  const loader = loaders[key];
  if (!loader) return;
  started.add(key);
  // Fire and forget. Errors are non-fatal — the real navigation will retry.
  loader().catch(() => {
    started.delete(key);
  });
}

// Best-effort: warm up a few high-traffic routes during browser idle time.
export function prefetchIdleRoutes(paths: string[]): void {
  if (typeof window === "undefined") return;
  const run = () => paths.forEach(prefetchRoute);
  const w = window as unknown as {
    requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
  };
  if (typeof w.requestIdleCallback === "function") {
    w.requestIdleCallback(run, { timeout: 2000 });
  } else {
    window.setTimeout(run, 1500);
  }
}
